export const config = {
  ionToken: import.meta.env.VITE_CESIUM_ION_TOKEN as string,

  // Public Google Sheet CSV with per-lot metadata (Ancho, Oeste, Este, Area).
  // Maintained by the sales team — fetched on app load and merged into LOTS
  // by id so the lot-details panel can show dimensions.
  lotsMetaCsvUrl:
    'https://docs.google.com/spreadsheets/d/e/2PACX-1vTEYkyLUCjwGNxrjrhw9C3QNvDDr3ZBhLvR3LHPuLMNnmPyja0PTh-Eizau-ogm04OOKLcEnRSIytc8/pub?gid=0&single=true&output=csv',

  // Marapa development site, Isla Margarita.
  // Irregular quadrilateral: top edge ~221m, bottom edge ~326m,
  // west edge ~619m, east edge ~387m. Area ~11.05 ha (~27.3 acres).
  site: {
    // Center = mean of the four corners.
    longitude: -63.991474080960834,
    latitude: 10.893982914804571,
    height: 0,

    corners: {
      topLeft:     { lat: 10.896038874483898, lon: -63.99280749748956 },
      // topRight: 62.00 m from topLeft along the original top-edge bearing
      // (~79° from north — mostly east, slightly north).
      topRight:    { lat: 10.896147004483898, lon: -63.99225098748956 },
      // bottomLeft: 620.00 m from topLeft along the original west-edge bearing
      // (~171° — mostly south, slightly east).
      bottomLeft:  { lat: 10.890495086483898, lon: -63.99195771948956 },
      // bottomRight: 540.00 m from topRight along the original east-edge bearing
      // (~172° — mostly south, slightly east).
      bottomRight: { lat: 10.891314238483898, lon: -63.99154049148956 },
    },
  },

  // Initial camera state — captured from a live session and pinned here so the
  // demo always opens to the same framing. Update by reading
  // `window.cesiumViewer.camera.{positionCartographic, heading, pitch, roll}`
  // from devtools, then converting radians → degrees.
  startView: {
    // Matches the 🏝️ Marapa preset in PositionHUD.tsx.
    longitude: -63.990208,
    latitude: 10.888882,
    height: 663.5,                   // meters above ellipsoid
    heading: 359.72,                 // degrees, 0 = north, 90 = east
    pitch: -52.91,                   // degrees, 0 = horizon, -90 = nadir
    roll: 0,
  },

  // Single source of truth for the 2D Leaflet fallback (low-data mode):
  // the initial view on first load AND the target of the 🔄 Reset button /
  // 🏝️ El Manglillo preset. Re-capture from devtools with:
  // `({ lat: leafletMap.getCenter().lat.toFixed(6),
  //    lon: leafletMap.getCenter().lng.toFixed(6),
  //    zoom: leafletMap.getZoom(),
  //    bearing: leafletMap.getBearing?.() ?? 0 })`.
  lowDataResetView: {
    longitude: -63.989595,
    latitude: 10.895318,
    zoom: 16,
    bearing: 7.11,
  },

  // Tight bounding box around the site, used by:
  // (a) the static raster fallback in 2D (offline / tiles unreachable)
  // (b) any future "fit-to-site" UI control
  // Tuple is [[southLat, westLon], [northLat, eastLon]] — Leaflet's
  // L.latLngBounds shape. Computed from site.corners.
  siteBboxLatLng: [
    [10.890495, -63.992807],
    [10.896147, -63.991541],
  ] as [[number, number], [number, number]],

  // Perimeter is drawn by connecting these ref-point numbers (P1, P2,
  // P11, P12, P13). Used by both the Cesium and Leaflet viewers.
  perimeterRefPoints: [1, 2, 11, 12, 13],

  // Locator map for the printable lot plan (src/ui/lotPlan.ts). `basemap` is a
  // hand-annotated satellite still bundled in public/; `bbox` is the exact
  // geographic extent it was exported at (see scripts/generate-lot-map.mjs), so
  // the perimeter/lot overlay lands pixel-accurate on it. Regenerate the image
  // with `node scripts/generate-lot-map.mjs --bg` if the frame ever changes.
  locator: {
    basemap: '/locator-basemap.jpg',
    bbox: { minLon: -63.9985, minLat: 10.8840, maxLon: -63.9695, maxLat: 10.9055 },
  },

  // Hard cap on the Cesium lazy-chunk import. If the network can't deliver
  // the chunk in this window we give up and reload in 2D mode (the
  // CesiumChunkBoundary catches the rejected Promise).
  cesiumChunkTimeoutMs: 6000,

  // Sun / time-of-day. Marapa is Venezuela (UTC-04:00, no DST).
  // Pinned date is fine — Earth's tilt at 10.9° N makes the sunrise/sunset
  // arc essentially constant year-round (~6:00 / ~18:30).
  sun: {
    pinnedDate: '2026-04-29',        // ISO date, no time
    timezoneOffset: '-04:00',
    initialHour: 16.0,               // 4 pm — late afternoon, warm tone, still bright
  },

  // 'photoreal' uses Google Photorealistic 3D Tiles (Cesium Ion asset 2275207).
  // 'terrain'   uses Cesium World Terrain + Bing/Sentinel imagery (fallback).
  // If photoreal fails to load at your coordinates, setup.ts auto-falls back.
  baseMap: 'photoreal' as 'photoreal' | 'terrain',

  // 'freefly' = v1: WASD + Q/E + mouse, no collision, no gravity.
  // 'grounded' = v2 (TODO): walk with gravity, F to toggle fly.
  cameraMode: 'freefly' as 'freefly' | 'grounded',

  // Contact info for the WhatsApp deep-link button in the lot-details panel.
  // `whatsappNumber` must be in E.164 format with country code, NO spaces,
  // dashes, or '+' (e.g. '584121234567' for a Venezuelan number).
  contact: {
    // E.164 with country code, no spaces / dashes / '+'.
    whatsappNumber: '584224107422',
    // Human-readable form of the same number, shown in the printable lot
    // plan's CTA. Keep both in sync — a buyer keeps the PDF, so a stale
    // printed number outlives any fix to the tappable link.
    whatsappDisplay: '+58 422-4107422',
    contactName: 'Manglillo',
  },

  player: {
    eyeHeight: 1.7, // meters, used by grounded mode
    walkSpeed: 120.0, // meters/second
    runMultiplier: 5.0, // hold Space to sprint (-> 600 m/s)
    scrollSpeed: 2.0, // meters per wheel notch (vertical move via scroll)
    mouseSensitivity: 0.002, // radians per pixel of mouse movement
  },
};
